import React, { useState, useEffect } from "react";

export default function PatOutcomeSelectionPanel({
  patDistillation,
  onSelectOutcome,
}) {
  const rawCandidates = patDistillation?.candidate_outcomes || [];
  const notes = patDistillation?.notes || null;

  const [candidates, setCandidates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    const next = rawCandidates.map((c) => ({
      id: crypto.randomUUID(),
      text: c,
    }));

    setCandidates(next);
    setSelectedId(next.length ? next[0].id : null);
  }, [patDistillation]);

  const selectedCandidate = candidates.find((c) => c.id === selectedId);
  const finalOutcome = selectedCandidate?.text?.trim() || "";

  const isSymbolicOutcome = /^[A-Za-z]$/.test(finalOutcome);
  const isSubstantiveOutcome = finalOutcome.length >= 3;

  const isValid = isSymbolicOutcome || isSubstantiveOutcome;
  const hasMultipleCandidates = candidates.length > 1;

  const handleEditCandidate = (id, newText) => {
    setCandidates((prev) =>
      prev.map((c) => (c.id === id ? { ...c, text: newText } : c)),
    );
  };

  const handleDeleteCandidate = (id) => {
    setCandidates((prev) => {
      const next = prev.filter((c) => c.id !== id);

      if (id === selectedId) {
        setSelectedId(next.length ? next[0].id : null);
      }

      return next;
    });
  };

  const handleAddCandidate = () => {
    const id = crypto.randomUUID();
    const newCandidate = { id, text: "" };

    setCandidates((prev) => [...prev, newCandidate]);
    setSelectedId(id);
  };

  return (
    <div className="mt-6 p-6 border border-red-700 rounded-lg text-sm text-gray-200 space-y-5">
      <div className="text-red-300 font-semibold text-lg">
        Confirm modelling outcome (Y)
      </div>

      <div className="text-gray-200">
        {hasMultipleCandidates
          ? "Multiple candidate outcomes (Y variables) were detected. Select one outcome below, edit it if needed, or add an alternative outcome."
          : "Select the outcome (Y) to carry forward, and edit it if needed."}
      </div>

      <div className="space-y-3">
        <div className="text-xs text-gray-400">
          Candidate outcomes (editable):
        </div>

        {candidates.length === 0 && (
          <div className="text-xs text-gray-500 italic">
            No candidate outcomes were supplied.
          </div>
        )}

        {candidates.map((o) => (
          <div
            key={o.id}
            className="flex items-center gap-2 bg-slate-900/40 border border-slate-700 rounded-lg px-3 py-2"
          >
            <input
              type="radio"
              name="selected_outcome"
              checked={selectedId === o.id}
              onChange={() => setSelectedId(o.id)}
              className="accent-red-500"
            />

            <input
              type="text"
              value={o.text}
              onChange={(e) => handleEditCandidate(o.id, e.target.value)}
              className="flex-1 bg-transparent text-gray-200 text-sm outline-none"
            />

            <button
              type="button"
              className="text-xs text-gray-400 hover:text-red-300"
              onClick={() => handleDeleteCandidate(o.id)}
              title="Remove this candidate outcome"
            >
              ✕
            </button>
          </div>
        ))}

        <button
          type="button"
          className="text-sm text-indigo-300 underline hover:text-indigo-300"
          onClick={handleAddCandidate}
        >
          + Add new candidate outcome
        </button>
      </div>

      <div className="text-xs text-gray-400 pt-2">
        Final outcome (Y) that will be used:
      </div>

      <div className="p-3 rounded-lg bg-slate-950 border border-slate-700 text-gray-100 text-sm">
        {finalOutcome || <span className="text-gray-500">(none selected)</span>}
      </div>

      <div className="pt-3">
        <button
          className={`px-5 py-2 rounded-lg text-sm font-semibold ${
            isValid
              ? "bg-red-600 hover:bg-red-500 text-white"
              : "bg-gray-700 text-gray-400 cursor-not-allowed"
          }`}
          disabled={!isValid}
          onClick={() => {
            if (!isValid) return;
            onSelectOutcome(finalOutcome);
          }}
        >
          Confirm outcome and continue
        </button>
      </div>

      <div className="text-xs text-gray-500 pt-1">
        Pearlian constraint: Each causal model requires one focal Y. Declaring Y
        defines what the model is about; different Y selections produce
        different causal models. Symbolic single-letter outcomes such as Y are
        permitted.
      </div>
    </div>
  );
}
